import { useEffect, useState } from "react";
import DataTable, { Column } from "@/components/ui/DataTable";
import { ButtonAdd, ButtonDelete, ButtonEdit } from "@/components/ui/button";
import { Supplier } from "@/redux/services/inventory/inventory.type";
import {
  useDeleteSupplierMutation,
  useGetSuppliersQuery,
} from "@/redux/services/inventory/supplier.api";
import { useModal, usePagination } from "@/lib/hooks";
import { usePermissions } from "@/hooks/usePermissions";
import { useDebounce } from "@/hooks/useDebounce";
import { formatPhoneNumber } from "@/lib/utils";
import { getPaymentTermsLabel } from "@/lib/paymentTerms";
import SupplierFormModal from "./SupplierFormModal";
import SupplierDetailsDrawer from "./SupplierDetailsDrawer";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { Trash2 } from "lucide-react";
import toast from "react-hot-toast";

interface SuppliersListProps {
  restaurantId: string;
}

const SuppliersList = ({ restaurantId }: SuppliersListProps) => {
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 300);
  const { page, limit, onChange } = usePagination(25);
  const [drawerSupplierId, setDrawerSupplierId] = useState<string | null>(null);
  const { canManage } = usePermissions();
  const canManageSuppliers = canManage("suppliers");

  useEffect(() => {
    onChange(limit, 1);
  }, [debouncedSearch]);

  const { data, isFetching } = useGetSuppliersQuery({
    restaurantId,
    query: {
      page,
      limit,
      ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
    },
  });
  const [deleteSupplier] = useDeleteSupplierMutation();
  const confirm = useConfirm();

  const {
    isOpen,
    item: supplierToEdit,
    closeModal,
    openModal,
    openEditModal,
  } = useModal<Supplier>();

  const handleDelete = async (supplier: Supplier) => {
    const ok = await confirm({
      title: `Supprimer "${supplier.name}" ?`,
      description: "Cette action est irréversible.",
      confirmText: "Supprimer",
      cancelText: "Annuler",
      variant: "destructive",
      icon: <Trash2 className="h-6 w-6" />,
    });
    if (!ok) return;
    try {
      const res = await deleteSupplier({ supplierUuid: supplier.id });
      if ("error" in res) {
        toast.error("Erreur lors de la suppression");
        return;
      }
      toast.success("Fournisseur supprimé avec succès");
    } catch {
      toast.error("Une erreur est survenue");
    }
  };

  const columns: Column[] = [
    {
      key: "name",
      label: "Nom",
      formatter: (_v, row: Supplier) => (
        <span className="font-medium text-gray-900">{row.name}</span>
      ),
    },
    {
      key: "contactName",
      label: "Contact",
      formatter: (_v, row: Supplier) => row.contactName ?? "—",
    },
    {
      key: "phone",
      label: "Téléphone",
      formatter: (_v, row: Supplier) =>
        row.phone ? (
          <a
            href={`tel:${row.phone}`}
            onClick={(e) => e.stopPropagation()}
            className="text-gray-900 hover:text-blue-700 hover:underline"
          >
            {formatPhoneNumber(row.phone)}
          </a>
        ) : (
          "—"
        ),
    },
    {
      key: "email",
      label: "Email",
      formatter: (_v, row: Supplier) =>
        row.email ? (
          <a
            href={`mailto:${row.email}`}
            onClick={(e) => e.stopPropagation()}
            className="text-gray-900 hover:text-blue-700 hover:underline"
          >
            {row.email}
          </a>
        ) : (
          "—"
        ),
    },
    {
      key: "paymentTerms",
      label: "Paiement",
      formatter: (_v, row: Supplier) => getPaymentTermsLabel(row.paymentTerms),
    },
    {
      key: "leadTimeDays",
      label: "Délai",
      formatter: (_v, row: Supplier) =>
        row.leadTimeDays !== null && row.leadTimeDays !== undefined
          ? `${row.leadTimeDays} j`
          : "—",
    },
    {
      key: "actions",
      label: "Actions",
      formatter: (_v, row: Supplier) => (
        <div className="flex items-center gap-1">
          {canManageSuppliers && (
            <ButtonEdit onClick={() => openEditModal(row)} />
          )}
          {canManageSuppliers && (
            <ButtonDelete onClick={() => handleDelete(row)} />
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="space-y-3">
        <div className="flex justify-end">
          {canManageSuppliers && (
            <ButtonAdd
              label="Ajouter un fournisseur"
              type="button"
              onClick={openModal}
            />
          )}
        </div>

        <DataTable
          data={data?.results ?? []}
          columns={columns}
          loading={isFetching}
          totalCount={data?.count ?? 0}
          currentPage={page}
          pageSize={limit}
          searchValue={searchInput}
          onSearch={setSearchInput}
          onPageChange={(p) => onChange(limit, p)}
          onPageSizeChange={(size) => onChange(size, 1)}
          onRowClick={(row: Supplier) => setDrawerSupplierId(row.id)}
          emptyMessage={
            searchInput
              ? "Aucun résultat pour votre recherche"
              : "Aucun fournisseur"
          }
        />
      </div>

      {isOpen && (
        <SupplierFormModal
          isOpen={isOpen}
          onClose={closeModal}
          supplierToEdit={supplierToEdit}
          restaurantId={restaurantId}
        />
      )}

      <SupplierDetailsDrawer
        isOpen={!!drawerSupplierId}
        onClose={() => setDrawerSupplierId(null)}
        restaurantId={restaurantId}
        supplierId={drawerSupplierId}
      />
    </>
  );
};

export default SuppliersList;
